import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import {
  PracticeAnswer,
  ProvSetStudent,
  getErrorMessage,
  getPracticeAnswers,
} from '../provApi';
import { extractPrimaryAnswer } from '../practiceUtils';

const BRAND_COLOR = '#1a365d';

interface PracticeSessionProps {
  set: ProvSetStudent;
  onBack: () => void;
}

function GlosResultsScreen({
  correctCount,
  total,
  onRetry,
  onBack,
}: {
  correctCount: number;
  total: number;
  onRetry: () => void;
  onBack: () => void;
}) {
  const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const stars = pct >= 90 ? 3 : pct >= 60 ? 2 : pct >= 30 ? 1 : 0;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', pb: 4 }}>
      <EmojiEventsIcon sx={{ fontSize: { xs: 56, sm: 72 }, color: '#FFD700', mb: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
        {[1, 2, 3].map((n) => (
          <StarIcon key={n} sx={{ fontSize: { xs: 36, sm: 44 }, color: n <= stars ? '#FFD700' : '#e0e0e0' }} />
        ))}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', sm: '2.2rem' }, color: BRAND_COLOR }}>
        {correctCount} av {total} rätt!
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
        <Button variant="outlined" size="large" onClick={onRetry} sx={{ borderRadius: 2, textTransform: 'none', minHeight: 48 }}>
          Öva igen
        </Button>
        <Button variant="contained" size="large" onClick={onBack} sx={{ borderRadius: 2, bgcolor: BRAND_COLOR, textTransform: 'none', minHeight: 48, '&:hover': { bgcolor: '#0f2744' } }}>
          Tillbaka
        </Button>
      </Box>
    </Box>
  );
}

function FlashcardPractice({
  set,
  answers,
  onBack,
}: {
  set: ProvSetStudent;
  answers: PracticeAnswer[];
  onBack: () => void;
}) {
  const questions = set.questions;
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState<Record<number, boolean>>({});
  const [done, setDone] = useState(false);
  const currentQ = questions[index];
  const answered = Object.keys(results).length;
  const progress = questions.length > 0 ? (answered / questions.length) * 100 : 0;
  const answerMap = Object.fromEntries(answers.map((row) => [row.questionId, extractPrimaryAnswer(row.answer)]));

  const handleMark = (correct: boolean) => {
    setResults((prev) => ({ ...prev, [index]: correct }));
    if (index < questions.length - 1) {
      setIndex(index + 1);
      setRevealed(false);
    } else {
      setDone(true);
    }
  };

  if (done) {
    return (
      <GlosResultsScreen
        correctCount={Object.values(results).filter(Boolean).length}
        total={questions.length}
        onRetry={() => { setIndex(0); setRevealed(false); setResults({}); setDone(false); }}
        onBack={onBack}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">Ord {index + 1} av {questions.length}</Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, mt: 1 }} />
      </Box>
      <Card
        elevation={3}
        sx={{ borderRadius: 4, mb: 3, minHeight: { xs: 220, sm: 280 }, display: 'flex', alignItems: 'center', cursor: revealed ? 'default' : 'pointer' }}
        onClick={() => !revealed && setRevealed(true)}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', width: '100%' }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.6rem', sm: '2rem' }, color: BRAND_COLOR }}>
            {currentQ.prompt}
          </Typography>
          {revealed && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#E8F5E9' }}>
              <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 700, color: '#2E7D32' }}>
                {answerMap[currentQ._id]}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      {!revealed ? (
        <Button variant="contained" fullWidth startIcon={<VisibilityIcon />} onClick={() => setRevealed(true)} sx={{ minHeight: 52, borderRadius: 3, bgcolor: BRAND_COLOR, textTransform: 'none' }}>
          Visa svaret
        </Button>
      ) : (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" startIcon={<ThumbDownIcon />} onClick={() => handleMark(false)} sx={{ flex: 1, minHeight: 56, borderRadius: 3, bgcolor: '#f44336', textTransform: 'none' }}>
            Kunde inte
          </Button>
          <Button variant="contained" startIcon={<ThumbUpIcon />} onClick={() => handleMark(true)} sx={{ flex: 1, minHeight: 56, borderRadius: 3, bgcolor: '#4CAF50', textTransform: 'none' }}>
            Kunde det!
          </Button>
        </Box>
      )}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button variant="text" onClick={onBack} sx={{ textTransform: 'none' }}>Tillbaka</Button>
      </Box>
    </Box>
  );
}

function MultipleChoicePractice({
  set,
  answers,
  onBack,
}: {
  set: ProvSetStudent;
  answers: PracticeAnswer[];
  onBack: () => void;
}) {
  const questions = set.questions;
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [results, setResults] = useState<Record<number, boolean>>({});
  const [done, setDone] = useState(false);
  const answerMap = Object.fromEntries(answers.map((row) => [row.questionId, extractPrimaryAnswer(row.answer)]));
  const currentQ = questions[index];
  const correctAnswer = currentQ ? (answerMap[currentQ._id] || '') : '';

  const options = useMemo(() => {
    if (!currentQ) return [];
    const correct = answerMap[currentQ._id] || '';
    if (!correct) return [];
    const others = questions
      .filter((q) => q._id !== currentQ._id)
      .map((q) => answerMap[q._id])
      .filter(Boolean)
      .filter((value) => value !== correct);
    const shuffledOthers = [...others].sort(() => Math.random() - 0.5).slice(0, 3);
    return [...shuffledOthers, correct].sort(() => Math.random() - 0.5);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ?._id, answers.length]);

  const handleSelect = (value: string) => {
    if (selected !== null) return;
    setSelected(value);
    const isCorrect = value === correctAnswer;
    setTimeout(() => {
      setResults((prev) => ({ ...prev, [index]: isCorrect }));
      if (index < questions.length - 1) {
        setIndex((current) => current + 1);
        setSelected(null);
      } else {
        setDone(true);
      }
    }, 1400);
  };

  if (done) {
    return (
      <GlosResultsScreen
        correctCount={Object.values(results).filter(Boolean).length}
        total={questions.length}
        onRetry={() => { setIndex(0); setSelected(null); setResults({}); setDone(false); }}
        onBack={onBack}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Ord {index + 1} av {questions.length}</Typography>
      <Card sx={{ borderRadius: 4, mb: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', sm: '2.2rem' }, color: BRAND_COLOR }}>
            {currentQ?.prompt}
          </Typography>
        </CardContent>
      </Card>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {options.map((opt, i) => {
          const showFeedback = selected !== null;
          const isCorrect = opt === correctAnswer;
          const isSelected = selected === opt;
          return (
            <Button
              key={`${opt}-${i}`}
              variant="outlined"
              onClick={() => handleSelect(opt)}
              disabled={selected !== null}
              sx={{
                minHeight: 64,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: showFeedback && isCorrect ? '#E8F5E9' : showFeedback && isSelected ? '#FFEBEE' : 'white',
              }}
            >
              {opt}
            </Button>
          );
        })}
      </Box>
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button variant="text" onClick={onBack} sx={{ textTransform: 'none' }}>Tillbaka</Button>
      </Box>
    </Box>
  );
}

function BrowsePractice({
  set,
  answers,
  onBack,
}: {
  set: ProvSetStudent;
  answers: PracticeAnswer[];
  onBack: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const currentQ = set.questions[index];
  const currentAnswer = answers.find((row) => row.questionId === currentQ?._id);

  useEffect(() => {
    setRevealed(false);
  }, [index]);

  if (!currentQ) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">Det finns inga frågor i det här setet.</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>Tillbaka</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', pb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {set.type === 'glosor' ? 'Ord' : 'Fråga'} {index + 1} av {set.questions.length}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={((index + 1) / set.questions.length) * 100}
          sx={{ height: 8, borderRadius: 4, mt: 1 }}
        />
      </Box>
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.4rem' }, mb: 2 }}>
            {currentQ.prompt}
          </Typography>
          {currentQ.imageUrl && (
            <Box component="img" src={currentQ.imageUrl} alt="" sx={{ maxWidth: '100%', maxHeight: 240, borderRadius: 2, mb: 2 }} />
          )}
          {!revealed ? (
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setRevealed(true)}
              sx={{ minHeight: 48, borderRadius: 2, bgcolor: BRAND_COLOR, textTransform: 'none' }}
            >
              Visa svar
            </Button>
          ) : (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#E8F5E9', border: '1px solid #A5D6A7' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>Rätt svar</Typography>
              {currentAnswer?.forms && currentAnswer.forms.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  {currentAnswer.forms.map((form) => (
                    <Typography key={form.label} sx={{ fontWeight: 600 }}>
                      {form.label}: {form.value}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 600, mt: 0.5 }}>{currentAnswer?.answer || ''}</Typography>
              )}
              {(currentAnswer?.acceptableAnswers || []).length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Godkända alternativ: {currentAnswer?.acceptableAnswers?.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, position: 'sticky', bottom: 8 }}>
        <IconButton
          onClick={() => setIndex((value) => Math.max(0, value - 1))}
          disabled={index === 0}
          sx={{ width: 48, height: 48, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Button variant="text" onClick={onBack} sx={{ textTransform: 'none' }}>Avsluta övning</Button>
        <IconButton
          onClick={() => setIndex((value) => Math.min(set.questions.length - 1, value + 1))}
          disabled={index === set.questions.length - 1}
          sx={{ width: 48, height: 48, bgcolor: BRAND_COLOR, color: 'white' }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function PracticeSession({ set, onBack }: PracticeSessionProps) {
  const [answers, setAnswers] = useState<PracticeAnswer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [glosMode, setGlosMode] = useState<'choose' | 'flashcard' | 'multiple-choice' | 'browse'>(
    set.type === 'glosor' ? 'choose' : 'browse'
  );

  useEffect(() => {
    let cancelled = false;
    getPracticeAnswers(set._id)
      .then((rows) => {
        if (!cancelled) setAnswers(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err));
      });
    return () => { cancelled = true; };
  }, [set._id]);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button onClick={onBack}>Tillbaka</Button>
      </Box>
    );
  }

  if (!answers) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ mt: 2 }}>Laddar övning...</Typography>
      </Box>
    );
  }

  if (set.type === 'glosor' && glosMode === 'choose') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: { xs: 3, sm: 5 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{set.title}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>Välj hur du vill öva</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Card sx={{ width: { xs: '100%', sm: 180 }, cursor: 'pointer' }} onClick={() => setGlosMode('flashcard')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MenuBookIcon sx={{ fontSize: 40, color: '#2E7D32' }} />
              <Typography sx={{ fontWeight: 700 }}>Flashcards</Typography>
              <Chip label="Öva" size="small" sx={{ mt: 1, bgcolor: '#E8F5E9', color: '#2E7D32' }} />
            </CardContent>
          </Card>
          <Card sx={{ width: { xs: '100%', sm: 180 }, cursor: 'pointer' }} onClick={() => setGlosMode('multiple-choice')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckBoxIcon sx={{ fontSize: 40, color: '#7C4DFF' }} />
              <Typography sx={{ fontWeight: 700 }}>Välj rätt</Typography>
              <Chip label="Öva" size="small" sx={{ mt: 1, bgcolor: '#EDE7F6', color: '#7C4DFF' }} />
            </CardContent>
          </Card>
          <Card sx={{ width: { xs: '100%', sm: 180 }, cursor: 'pointer' }} onClick={() => setGlosMode('browse')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <QuizIcon sx={{ fontSize: 40, color: BRAND_COLOR }} />
              <Typography sx={{ fontWeight: 700 }}>Bläddra</Typography>
              <Chip label="Visa svar" size="small" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
        <Button onClick={onBack} sx={{ mt: 3, textTransform: 'none' }}>Tillbaka till listan</Button>
      </Box>
    );
  }

  if (set.type === 'glosor' && glosMode === 'flashcard') {
    return <FlashcardPractice set={set} answers={answers} onBack={() => setGlosMode('choose')} />;
  }
  if (set.type === 'glosor' && glosMode === 'multiple-choice') {
    return <MultipleChoicePractice set={set} answers={answers} onBack={() => setGlosMode('choose')} />;
  }

  return <BrowsePractice set={set} answers={answers} onBack={onBack} />;
}
